/**
 * Client-side usage refresh, shared by every Supported Client Surface.
 *
 * @module state/usage
 */
import type { EnvironmentId, UsageSummaryInput } from "@afar-research/contracts";
import * as Schema from "effect/Schema";
import type { AtomRegistry } from "effect/unstable/reactivity";

import { EnvironmentRpcUnavailableError } from "../rpc/client.ts";
import type { createEnvironmentPresentationAtoms } from "./presentation.ts";
import { executeAtomQuery, runAtomCommand, squashAtomCommandFailure } from "./runtime.ts";
import type { createServerEnvironmentAtoms } from "./server.ts";

const isEnvironmentRpcUnavailable = Schema.is(EnvironmentRpcUnavailableError);

const limitsRefreshAfter = new Map<EnvironmentId, number>();
const limitsRefreshes = new Map<EnvironmentId, Promise<unknown>>();

/**
 * Re-probe one AFAR Environment's subscription limits, at most once every five
 * minutes per client when the refresh is automatic.
 *
 * A manual refresh still waits on a check already in flight rather than
 * starting a second one; an automatic refresh simply declines.
 */
export async function refreshUsageLimits<A>(
  environmentId: EnvironmentId,
  refresh: () => Promise<A>,
  automatic = false,
): Promise<A | undefined> {
  const pending = limitsRefreshes.get(environmentId);
  if (pending !== undefined) {
    return automatic ? undefined : ((await pending) as A);
  }
  const refreshAfter = limitsRefreshAfter.get(environmentId) ?? 0;
  // @effect-diagnostics-next-line globalDate:off
  if (automatic && Date.now() < refreshAfter) return;
  const current = Promise.resolve()
    .then(refresh)
    .finally(() => {
      limitsRefreshes.delete(environmentId);
      // Including after a failure: a failing environment must not be re-probed
      // on every render.
      // @effect-diagnostics-next-line globalDate:off
      limitsRefreshAfter.set(environmentId, Date.now() + 5 * 60_000);
    });
  limitsRefreshes.set(environmentId, current);
  return await current;
}

/**
 * Refresh pricing, then await each selected AFAR Environment's rescan.
 *
 * The query is invalidated even when the rate refresh fails, so a reconnecting
 * client cannot be served the summary priced by the old table.
 */
export async function refreshUsage({
  registry,
  server,
  presentations,
  environmentIds,
  input,
}: {
  registry: AtomRegistry.AtomRegistry;
  server: Pick<
    ReturnType<typeof createServerEnvironmentAtoms>,
    "usageSummary" | "refreshUsageRates"
  >;
  presentations: Pick<ReturnType<typeof createEnvironmentPresentationAtoms>, "presentationAtom">;
  environmentIds: readonly EnvironmentId[];
  input: UsageSummaryInput;
}): Promise<void> {
  await Promise.all(
    environmentIds.map(async (environmentId) => {
      const query = server.usageSummary({ environmentId, input });
      const presentation = presentations.presentationAtom(environmentId);
      const isConnected = () => registry.get(presentation)?.connection.phase === "ready";
      const ratesResult = await runAtomCommand(
        registry,
        server.refreshUsageRates,
        { environmentId, input: {} },
        { reportFailure: false },
      );
      const sessionUnavailable =
        ratesResult._tag === "Failure" &&
        isEnvironmentRpcUnavailable(squashAtomCommandFailure(ratesResult));
      registry.refresh(query);
      // An environment that dropped while its rates were refreshing has
      // nothing to rescan; the invalidation above still stands, so a later
      // reconnect re-reads rather than serving the stale summary.
      if (sessionUnavailable || !isConnected()) return;
      await executeAtomQuery(registry, query, { reportFailure: false });
    }),
  );
}
